import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { Icon } from './Icons';

const CHALLENGE_TYPES = [
  { id: 'steps', label: 'Step Challenge', icon: 'activity', unit: 'steps', desc: 'Most steps in 7 days wins', target: 70000 },
  { id: 'water', label: 'Hydration Challenge', icon: 'droplet', unit: 'glasses', desc: 'Most water glasses in 7 days', target: 56 },
  { id: 'training', label: 'Training Challenge', icon: 'dumbbell', unit: 'sessions', desc: 'Most training sessions in 7 days', target: 5 },
];

function ProgressBar({ value, max, color = 'var(--primary)' }) {
  const pct = Math.min(100, max > 0 ? Math.round((value / max) * 100) : 0);
  return (
    <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 3, transition: 'width .6s ease' }} />
    </div>
  );
}

function LeaderboardRow({ rank, name, value, unit, target, isYou }) {
  const medal = rank === 1 ? { color: '#f59e0b', label: '1st' } : rank === 2 ? { color: '#94a3b8', label: '2nd' } : rank === 3 ? { color: '#d97706', label: '3rd' } : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', padding: '.75rem 1rem', background: isYou ? 'rgba(0,174,239,0.06)' : 'transparent', borderRadius: 'var(--radius)', border: isYou ? '1px solid rgba(0,174,239,0.2)' : '1px solid transparent', marginBottom: '.4rem' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: medal ? medal.color + '20' : 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {medal
          ? <span style={{ fontSize: '.7rem', fontWeight: 700, color: medal.color }}>{medal.label}</span>
          : <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 500 }}>{rank}</span>
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.3rem' }}>
          <span style={{ fontSize: '.82rem', fontWeight: isYou ? 600 : 400, color: isYou ? 'var(--primary)' : 'var(--text)' }}>
            {name}{isYou ? ' (you)' : ''}
          </span>
          <span style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--text2)' }}>
            {value.toLocaleString()} <span style={{ fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300 }}>{unit}</span>
          </span>
        </div>
        <ProgressBar value={value} max={target} color={medal ? medal.color : isYou ? 'var(--primary)' : 'var(--text3)'} />
      </div>
    </div>
  );
}

export default function TeamChallenge({ companyCode, companyData }) {
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myEntry, setMyEntry] = useState(null);
  const [inputVal, setInputVal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedType, setSelectedType] = useState('steps');
  const [useCustom, setUseCustom] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [customTarget, setCustomTarget] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [isHR, setIsHR] = useState(false);

  const user = auth.currentUser;
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadChallenge();
    checkHR();
  }, []);

  async function checkHR() {
    if (!user) return;
    try {
      // Check role from top-level user profile
      const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'profile'));
      if (snap.exists()) {
        const data = snap.data();
        if (data.role === 'hr' || data.role === 'admin') { setIsHR(true); return; }
      }
      // Also check via email pattern for HR test accounts
      if (user.email && (user.email.includes('hr-') || user.email === 'contact@levarewellbeing.com')) {
        setIsHR(true);
      }
    } catch(e) { console.error('checkHR error:', e); }
  }

  async function loadChallenge() {
    if (!companyCode) return;
    setLoading(true);
    try {
      const challengeSnap = await getDoc(doc(db, 'challengeActive', companyCode));
      if (challengeSnap.exists()) {
        const challenge = challengeSnap.data();
        setActiveChallenge(challenge);
        await loadLeaderboard(challenge);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function loadLeaderboard(challenge) {
    if (!companyCode) return;
    try {
      const entriesSnap = await getDocs(
        query(collection(db, 'challengeEntries'), where('companyCode', '==', companyCode))
      );
      const entries = entriesSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.value || 0) - (a.value || 0));
      setLeaderboard(entries);
      const mine = entries.find(e => e.userId === user?.uid);
      if (mine) { setMyEntry(mine); setInputVal(mine.value?.toString() || ''); }
    } catch(e) { console.error(e); }
  }

  async function createChallenge() {
    if (!companyCode || !isHR) return;
    setCreating(true);
    try {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 7);
      let challenge;
      if (useCustom) {
        if (!customLabel || !customUnit || !customTarget) {
          alert('Please fill in challenge name, unit and target.');
          setCreating(false); return;
        }
        challenge = {
          type: 'custom', label: customLabel, icon: 'target',
          unit: customUnit, desc: customDesc || customLabel,
          target: parseInt(customTarget) || 100,
          startDate: today, endDate: endDate.toISOString().split('T')[0],
          createdBy: user.uid, createdAt: serverTimestamp(),
          companyCode, active: true
        };
      } else {
        const type = CHALLENGE_TYPES.find(t => t.id === selectedType);
        challenge = {
          type: type.id, label: type.label, icon: type.icon,
          unit: type.unit, desc: type.desc, target: type.target,
          startDate: today, endDate: endDate.toISOString().split('T')[0],
          createdBy: user.uid, createdAt: serverTimestamp(),
          companyCode, active: true
        };
      }
      await setDoc(doc(db, 'challengeActive', companyCode), challenge);
      setActiveChallenge(challenge);
    } catch(e) {
      console.error('Create challenge error:', e);
      alert('Error creating challenge: ' + e.message);
    }
    setCreating(false);
  }

  async function submitEntry() {
    if (!inputVal || !activeChallenge || !user) return;
    setSubmitting(true);
    try {
      const profileSnap = await getDoc(doc(db, 'users', user.uid, 'latest', 'profile'));
      const name = profileSnap.exists()
        ? (profileSnap.data().firstName || 'Anonymous')
        : 'Anonymous';
      const entry = {
        userId: user.uid, name,
        value: parseInt(inputVal) || 0,
        unit: activeChallenge.unit,
        companyCode,
        updatedAt: serverTimestamp(), date: today
      };
      await setDoc(doc(db, 'challengeEntries', companyCode + '_' + user.uid), entry);
      setMyEntry(entry);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 2000);
      await loadLeaderboard(activeChallenge);
    } catch(e) { console.error(e); }
    setSubmitting(false);
  }

  const type = CHALLENGE_TYPES.find(t => t.id === activeChallenge?.type) || CHALLENGE_TYPES[0];
  const daysLeft = activeChallenge?.endDate
    ? Math.max(0, Math.ceil((new Date(activeChallenge.endDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text3)', fontSize: '.82rem' }}>Loading challenge…</div>;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.3rem' }}>Team Challenge</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Friendly competition to drive team health. Submit your progress and see how you rank.</p>
      </div>

      {!activeChallenge ? (
        <div>
          {isHR ? (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
              <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Start a New Challenge</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '1.25rem' }}>
                {CHALLENGE_TYPES.map(t => (
                  <button key={t.id} onClick={() => setSelectedType(t.id)} style={{ display: 'flex', alignItems: 'center', gap: '.85rem', padding: '.85rem 1rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: selectedType === t.id ? 'var(--primary)' : 'var(--border2)', background: selectedType === t.id ? 'rgba(0,174,239,0.06)' : 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: selectedType === t.id ? 'rgba(0,174,239,0.1)' : 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={t.icon} size={16} color={selectedType === t.id ? 'var(--primary)' : 'var(--text3)'} />
                    </div>
                    <div>
                      <div style={{ fontSize: '.85rem', fontWeight: 500, color: selectedType === t.id ? 'var(--primary)' : 'var(--text)' }}>{t.label}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{t.desc} · Target: {t.target.toLocaleString()} {t.unit}</div>
                    </div>
                  </button>
                ))}
              </div>
              {/* Custom challenge toggle */}
              <div style={{ marginTop: '.25rem', marginBottom: '.75rem' }}>
                <button onClick={() => setUseCustom(v => !v)} style={{ fontSize: '.72rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0, display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                  <Icon name={useCustom ? 'chevron-up' : 'chevron-down'} size={12} />
                  {useCustom ? 'Use preset challenge instead' : 'Create a custom challenge'}
                </button>
              </div>

              {useCustom && (
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '.75rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                  <div>
                    <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>Challenge Name *</label>
                    <input value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="e.g. Mindfulness Minutes" style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.5rem .75rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.82rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                    <div>
                      <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>Unit *</label>
                      <input value={customUnit} onChange={e => setCustomUnit(e.target.value)} placeholder="e.g. minutes" style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.5rem .75rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.82rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>7-Day Target *</label>
                      <input type="number" value={customTarget} onChange={e => setCustomTarget(e.target.value)} placeholder="e.g. 210" style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.5rem .75rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.82rem', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>Description (optional)</label>
                    <input value={customDesc} onChange={e => setCustomDesc(e.target.value)} placeholder="e.g. Log your daily mindfulness minutes" style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.5rem .75rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.82rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}

              <button onClick={createChallenge} disabled={creating} style={{ width: '100%', padding: '.75rem', borderRadius: 'var(--radius)', border: 'none', background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.88rem', fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem' }}>
                <Icon name="zap" size={16} color="#080f18" />
                {creating ? 'Starting…' : 'Start 7-Day Challenge'}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text3)' }}>
              <Icon name="target" size={36} color="var(--text3)" style={{ marginBottom: '1rem' }} />
              <h3 style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', color: 'var(--text)', marginBottom: '.5rem' }}>No active challenge</h3>
              <p style={{ fontSize: '.82rem', fontWeight: 300 }}>Your HR team will start a challenge when ready. Check back soon.</p>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Active challenge header */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(0,174,239,0.1)', border: '1px solid rgba(0,174,239,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={activeChallenge.icon || type.icon} size={20} color="var(--primary)" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.2rem' }}>{activeChallenge.label}</div>
              <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300 }}>{activeChallenge.desc} · Target: {activeChallenge.target?.toLocaleString()} {activeChallenge.unit}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: daysLeft <= 2 ? 'var(--danger)' : 'var(--primary)' }}>{daysLeft}d</div>
              <div style={{ fontSize: '.68rem', color: 'var(--text3)' }}>remaining</div>
            </div>
          </div>

          {/* Submit your entry */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.85rem' }}>Your Entry</div>
            <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ fontSize: '.7rem', color: 'var(--text3)', display: 'block', marginBottom: '.3rem' }}>
                  Total {activeChallenge.unit} this week
                </label>
                <input type="number" value={inputVal} onChange={e => setInputVal(e.target.value)} placeholder="e.g. 42000" min="0"
                  style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .85rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.88rem', outline: 'none' }} />
              </div>
              <button onClick={submitEntry} disabled={submitting || !inputVal} style={{ padding: '.65rem 1.5rem', borderRadius: 'var(--radius)', border: 'none', background: submitted ? 'var(--success)' : 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: submitting || !inputVal ? 'not-allowed' : 'pointer', opacity: submitting || !inputVal ? .7 : 1, whiteSpace: 'nowrap', alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <Icon name={submitted ? 'check-circle' : 'send'} size={14} color="#080f18" />
                {submitted ? 'Submitted!' : submitting ? 'Submitting…' : myEntry ? 'Update Entry' : 'Submit Entry'}
              </button>
            </div>
            {myEntry && (
              <div style={{ marginTop: '.75rem', padding: '.5rem .75rem', background: 'var(--success-dim)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                <Icon name="check-circle" size={13} color="var(--success)" />
                Your current entry: {myEntry.value?.toLocaleString()} {activeChallenge.unit}
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Leaderboard</div>
              <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{leaderboard.length} participant{leaderboard.length !== 1 ? 's' : ''}</span>
            </div>
            {leaderboard.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text3)', fontSize: '.82rem' }}>
                No entries yet — be the first to submit!
              </div>
            ) : (
              leaderboard.map((entry, i) => (
                <LeaderboardRow
                  key={entry.id}
                  rank={i + 1}
                  name={entry.name}
                  value={entry.value || 0}
                  unit={activeChallenge.unit}
                  target={activeChallenge.target}
                  isYou={entry.userId === user?.uid}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
